import React, { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Newspaper,
  ExternalLink,
  Eye,
  EyeOff,
} from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_OFF,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const CATEGORIES = ["Actualités", "Éducation", "Carrières", "Tutoriels", "Événements", "Parents"];

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  image_url: "",
  category: "Actualités",
  author: "",
  published: false,
  published_at: "",
};

function slugify(text = "") {
  return (
    String(text)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 120) || "article"
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch("/api/admin/posts");
      if (res.ok) setPosts(await res.json());
      else toast.error("Impossible de charger les articles");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      excerpt: item.excerpt || "",
      content: item.content || "",
      image_url: item.image_url || "",
      category: item.category || "Actualités",
      author: item.author || "",
      published: item.published === true,
      published_at: item.published_at ? String(item.published_at).slice(0, 10) : "",
    });
    setSlugTouched(true);
    setErrors({});
    setDialogOpen(true);
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = "Le titre est requis";
    if (!form.excerpt.trim()) next.excerpt = "Le chapô est requis";
    if (!form.content.trim()) next.content = "Le contenu est requis";
    if (form.slug && !slugify(form.slug)) next.slug = "Slug invalide";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || slugify(form.title),
      excerpt: form.excerpt.trim(),
      content: form.content.trim(),
      image_url: form.image_url.trim(),
      category: form.category,
      author: form.author.trim(),
      published: form.published,
      ...(form.published_at ? { published_at: form.published_at } : {}),
    };

    try {
      const url = editing ? `/api/admin/posts/${editing.id}` : "/api/admin/posts";
      const method = editing ? "PUT" : "POST";
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editing ? "Article mis à jour" : "Article créé");
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/admin/posts/${deleteTarget.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
      toast.success("Article supprimé");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Impossible de supprimer");
    } finally {
      setDeleting(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "title" && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <div>
      <AdminPageHeader
        icon={Newspaper}
        title="Blog"
        description="Créez et publiez les articles affichés sur le blog public"
        action={
          <Button onClick={openCreate} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvel article
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des articles..." />
      ) : posts.length === 0 ? (
        <AdminEmptyState
          icon={Newspaper}
          title="Aucun article"
          description="Créez votre premier article pour alimenter le blog public."
          action={
            <Button onClick={openCreate} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvel article
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={posts.map((item) => (
            <AdminMobileCard
              key={item.id}
              title={item.title}
              subtitle={item.category || "Actualités"}
              onClick={() => openEdit(item)}
              badge={
                <Badge className={item.published ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                  {item.published ? "Publié" : "Brouillon"}
                </Badge>
              }
              meta={[
                { label: "Auteur", value: item.author },
                { label: "Date", value: formatDate(item.published_at || item.created_at) },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(item)} aria-label="Supprimer">
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Article</TableHead>
                <TableHead className={adminTableHeadClass}>Catégorie</TableHead>
                <TableHead className={adminTableHeadClass}>Auteur</TableHead>
                <TableHead className={adminTableHeadClass}>Date</TableHead>
                <TableHead className={adminTableHeadClass}>Lecture</TableHead>
                <TableHead className={adminTableHeadClass}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((item) => (
                <TableRow key={item.id} className={adminClickableRowClass} onClick={() => openEdit(item)}>
                  <TableCell>
                    <div className="flex items-center gap-3 min-w-0">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt=""
                          className="w-12 h-12 rounded-lg object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          <Newspaper className="h-5 w-5 text-slate-600" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-slate-900 font-medium truncate max-w-xs">{item.title}</p>
                        <p className="text-slate-500 text-xs truncate max-w-xs">{item.slug || item.id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-[rgba(58,0,224,0.2)] text-[#3a00e0]">
                      {item.category || "Actualités"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-400">{item.author || "—"}</TableCell>
                  <TableCell className="text-slate-400 tabular-nums">
                    {formatDate(item.published_at || item.created_at)}
                  </TableCell>
                  <TableCell className="text-slate-400 tabular-nums">
                    {item.reading_time ? `${item.reading_time} min` : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        item.published
                          ? ADMIN_BADGE_OK
                          : "bg-slate-100 text-slate-500"
                      }
                    >
                      {item.published ? (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          Publié
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          Brouillon
                        </>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {item.published && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        aria-label="Voir sur le site"
                      >
                        <a href={`/blog/${item.slug || item.id}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(item)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(item)}
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'article" : "Nouvel article"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className={ADMIN_LABEL}>Titre *</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                className={`${ADMIN_FIELD} ${errors.title ? "border-red-500" : ""}`}
              />
              {errors.title && <p className="text-red-600 text-xs mt-1">{errors.title}</p>}
            </div>

            <div>
              <Label className={ADMIN_LABEL}>Slug (URL)</Label>
              <Input
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  updateField("slug", e.target.value);
                }}
                placeholder="mon-article"
                className={`${ADMIN_FIELD} font-mono text-sm ${errors.slug ? "border-red-500" : ""}`}
              />
              {errors.slug && <p className="text-red-600 text-xs mt-1">{errors.slug}</p>}
              <p className="text-slate-500 text-xs mt-1">Généré automatiquement depuis le titre si vide.</p>
            </div>

            <div>
              <Label className={ADMIN_LABEL}>Chapô / extrait *</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => updateField("excerpt", e.target.value)}
                className={`${ADMIN_FIELD} ${errors.excerpt ? "border-red-500" : ""}`}
                rows={3}
              />
              {errors.excerpt && <p className="text-red-600 text-xs mt-1">{errors.excerpt}</p>}
            </div>

            <div>
              <Label className={ADMIN_LABEL}>Contenu *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => updateField("content", e.target.value)}
                className={`${ADMIN_FIELD} min-h-[200px] ${errors.content ? "border-red-500" : ""}`}
                rows={10}
              />
              {errors.content && <p className="text-red-600 text-xs mt-1">{errors.content}</p>}
            </div>

            <div>
              <Label className={ADMIN_LABEL}>Image de couverture (URL)</Label>
              <Input
                value={form.image_url}
                onChange={(e) => updateField("image_url", e.target.value)}
                placeholder="https://... ou /images/..."
                className={ADMIN_FIELD}
              />
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="Aperçu"
                  className="mt-3 w-full max-h-40 object-cover rounded-lg border border-slate-200"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={ADMIN_LABEL}>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => updateField("category", v)}>
                  <SelectTrigger className={`${ADMIN_FIELD}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} className="text-slate-900">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={ADMIN_LABEL}>Auteur</Label>
                <Input
                  value={form.author}
                  onChange={(e) => updateField("author", e.target.value)}
                  placeholder="Équipe DigiRobot"
                  className={ADMIN_FIELD}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <Label className={ADMIN_LABEL}>Date de publication</Label>
                <Input
                  type="date"
                  value={form.published_at}
                  onChange={(e) => updateField("published_at", e.target.value)}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <Label className={ADMIN_LABEL}>Publié</Label>
                  <p className="text-slate-500 text-xs">Visible sur le blog public</p>
                </div>
                <Switch
                  checked={form.published}
                  onCheckedChange={(checked) => updateField("published", checked)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={save} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title="Supprimer cet article ?"
        description={`« ${deleteTarget?.title || "Cet article"} » sera définitivement supprimé.`}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
